"use client";

import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { Audio, Video } from "@remotion/media";

import {
  AbsoluteFill,
  Html5Audio,
  Html5Video,
  interpolate,
  spring,
  useRemotionEnvironment,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

import type { SubtitleGroup } from "../../lib/learning";
import {
  MATERIAL_VIDEO_FONT_FAMILY,
  computeMaterialVideoHighlightState,
  formatMaterialVideoClock,
  getMaterialVideoActiveWordKey,
  getMaterialVideoActiveGroupIndices,
  MATERIAL_VIDEO_LAYOUT,
  MATERIAL_VIDEO_RENDER_CONFIG,
  MATERIAL_VIDEO_SUBTITLE_VIEWPORT_HEIGHT,
  materialVideoHasCjk,
} from "../../lib/material-video-preview";

export type MaterialVideoRemotionPreviewProps = {
  title: string;
  mediaUrl: string;
  audioUrl?: string;
  groups: SubtitleGroup[];
  totalDuration: number;
  showRomaji: boolean;
  showFurigana: boolean;
  showTimestamp: boolean;
  showScrollingSubtitles: boolean;
  karaokeMode: boolean;
  useSeparateAudioTrack: boolean;
};

export function MaterialVideoRemotionPreview(
  props: MaterialVideoRemotionPreviewProps,
) {
  const {
    title,
    mediaUrl,
    audioUrl,
    groups,
    totalDuration,
    showRomaji,
    showFurigana,
    showTimestamp,
    showScrollingSubtitles,
    karaokeMode,
    useSeparateAudioTrack,
  } = props;
  const { isRendering } = useRemotionEnvironment();
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const currentTime = fps > 0 ? frame / fps : 0;
  const progress =
    totalDuration > 0 ? Math.min(1, currentTime / totalDuration) : 0;
  const activeGroupIndices = getMaterialVideoActiveGroupIndices(
    groups,
    currentTime,
  );
  const activeGroupIndex =
    activeGroupIndices[0] ?? Math.max(0, groups.length - 1);
  const highlightState = computeMaterialVideoHighlightState(
    groups,
    currentTime,
    karaokeMode,
  );
  const activeWordKey = getMaterialVideoActiveWordKey(groups, currentTime);
  const activeHeadline = groups[activeGroupIndex]?.subtitle || title;
  const subtitleViewportRef = useRef<HTMLDivElement | null>(null);
  const wordRefs = useRef<Record<string, HTMLSpanElement | null>>({});
  const [wordCenters, setWordCenters] = useState<
    Array<{ centerTime: number; centerY: number }>
  >([]);
  const [wordCenterMap, setWordCenterMap] = useState<Record<string, number>>(
    {},
  );
  const reveal = spring({
    fps,
    frame,
    config: {
      damping: 18,
      stiffness: 140,
      mass: 0.9,
    },
  });
  const timelineAnchorY =
    MATERIAL_VIDEO_SUBTITLE_VIEWPORT_HEIGHT *
    MATERIAL_VIDEO_LAYOUT.subtitles.centerAnchorRatio;
  const timelineViewportPaddingTop = timelineAnchorY;
  const timelineViewportPaddingBottom =
    MATERIAL_VIDEO_SUBTITLE_VIEWPORT_HEIGHT - timelineAnchorY;
  const showDebugGuides =
    process.env.NODE_ENV === "development" && !isRendering;
  const revealProgress = reveal;
  const visibleTimelineGroups = useMemo(
    () =>
      groups.map((group, index) => ({
        group,
        index,
      })),
    [groups],
  );

  useLayoutEffect(() => {
    const viewport = subtitleViewportRef.current;
    if (!viewport || !showScrollingSubtitles) {
      return;
    }

    function getCenterYRelativeToTrack(element: HTMLElement) {
      let centerY = element.offsetHeight / 2;
      let node: HTMLElement | null = element;
      const viewportElement = viewport;

      while (
        node &&
        node !== viewportElement &&
        node.offsetParent instanceof HTMLElement
      ) {
        centerY += node.offsetTop;
        node = node.parentElement;
      }

      return centerY;
    }

    let firstFrame = 0;
    let secondFrame = 0;

    const captureMeasurements = () => {
      const nextPoints = visibleTimelineGroups
        .flatMap(({ group }) =>
          group.words.map((word) => {
            const wordKey = `${group.id}:${word.id}`;
            const element = wordRefs.current[wordKey];
            if (!element) return null;
            return {
              wordKey,
              centerTime: (word.start + word.end) / 2,
              centerY: getCenterYRelativeToTrack(element),
            };
          }),
        )
        .filter(
          (
            point,
          ): point is {
            wordKey: string;
            centerTime: number;
            centerY: number;
          } => Boolean(point),
        )
        .sort((left, right) => left.centerTime - right.centerTime);

      const nextCenterMap = Object.fromEntries(
        nextPoints.map((point) => [point.wordKey, point.centerY]),
      );
      const nextWordCenters = nextPoints.map(({ centerTime, centerY }) => ({
        centerTime,
        centerY,
      }));

      setWordCenters((current) => {
        if (
          current.length === nextWordCenters.length &&
          current.every(
            (point, index) =>
              point.centerTime === nextWordCenters[index]?.centerTime &&
              point.centerY === nextWordCenters[index]?.centerY,
          )
        ) {
          return current;
        }

        return nextWordCenters;
      });
      setWordCenterMap((current) => {
        const currentKeys = Object.keys(current);
        const nextKeys = Object.keys(nextCenterMap);
        if (
          currentKeys.length === nextKeys.length &&
          nextKeys.every((key) => current[key] === nextCenterMap[key])
        ) {
          return current;
        }

        return nextCenterMap;
      });
    };

    firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(captureMeasurements);
    });

    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, [showFurigana, showRomaji, showScrollingSubtitles, visibleTimelineGroups]);

  const subtitleTrackShift = useMemo(() => {
    if (!showScrollingSubtitles || !wordCenters.length) {
      return 0;
    }

    const viewportAnchorY = MATERIAL_VIDEO_SUBTITLE_VIEWPORT_HEIGHT / 2;
    const activeCenterY =
      activeWordKey && wordCenterMap[activeWordKey] != null
        ? wordCenterMap[activeWordKey]
        : null;

    let targetCenterY = activeCenterY ?? null;

    if (targetCenterY === null) {
      if (currentTime <= wordCenters[0].centerTime) {
        targetCenterY = wordCenters[0].centerY;
      } else {
        const lastPoint = wordCenters.at(-1);
        if (lastPoint && currentTime >= lastPoint.centerTime) {
          targetCenterY = lastPoint.centerY;
        } else {
          for (let index = 1; index < wordCenters.length; index += 1) {
            const previousPoint = wordCenters[index - 1];
            const nextPoint = wordCenters[index];
            if (currentTime <= nextPoint.centerTime) {
              const progress =
                nextPoint.centerTime === previousPoint.centerTime
                  ? 1
                  : (currentTime - previousPoint.centerTime) /
                    (nextPoint.centerTime - previousPoint.centerTime);
              targetCenterY =
                previousPoint.centerY +
                (nextPoint.centerY - previousPoint.centerY) * progress;
              break;
            }
          }
        }
      }
    }

    return targetCenterY == null ? 0 : viewportAnchorY - targetCenterY;
  }, [
    activeWordKey,
    currentTime,
    showScrollingSubtitles,
    wordCenterMap,
    wordCenters,
  ]);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: MATERIAL_VIDEO_LAYOUT.backgroundColor,
        overflow: "hidden",
        fontFamily: MATERIAL_VIDEO_FONT_FAMILY,
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        textRendering: "optimizeLegibility",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: MATERIAL_VIDEO_LAYOUT.video.x,
          right: MATERIAL_VIDEO_LAYOUT.video.x,
          top: MATERIAL_VIDEO_LAYOUT.video.y,
          height: MATERIAL_VIDEO_LAYOUT.video.height,
          borderRadius: MATERIAL_VIDEO_LAYOUT.video.radius,
          overflow: "hidden",
          backgroundColor: MATERIAL_VIDEO_LAYOUT.video.backgroundColor,
          boxShadow: "none",
        }}
      >
        {isRendering ? (
          <Video
            src={mediaUrl}
            muted={useSeparateAudioTrack}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        ) : (
          <Html5Video
            acceptableTimeShiftInSeconds={
              MATERIAL_VIDEO_RENDER_CONFIG.previewAcceptableTimeShiftInSeconds
            }
            pauseWhenBuffering
            src={mediaUrl}
            muted={useSeparateAudioTrack}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        )}
        {useSeparateAudioTrack && audioUrl ? (
          isRendering ? (
            <Audio disallowFallbackToHtml5Audio src={audioUrl} />
          ) : (
            <Html5Audio pauseWhenBuffering src={audioUrl} />
          )
        ) : null}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: MATERIAL_VIDEO_LAYOUT.video.progressHeight,
            backgroundColor: MATERIAL_VIDEO_LAYOUT.video.progressTrackColor,
          }}
        >
          <div
            style={{
              width: `${progress * 100}%`,
              height: "100%",
              background: `linear-gradient(90deg, ${MATERIAL_VIDEO_LAYOUT.video.progressFillStart}, ${MATERIAL_VIDEO_LAYOUT.video.progressFillEnd})`,
            }}
          />
        </div>
      </div>

      {showScrollingSubtitles ? (
        <div
          data-current-time={showDebugGuides ? String(currentTime) : undefined}
          data-active-word-key={
            showDebugGuides ? (activeWordKey ?? "") : undefined
          }
          data-subtitle-viewport={showDebugGuides ? "true" : undefined}
          ref={subtitleViewportRef}
          style={{
            position: "absolute",
            left: MATERIAL_VIDEO_LAYOUT.subtitles.left,
            right: MATERIAL_VIDEO_LAYOUT.subtitles.right,
            top: MATERIAL_VIDEO_LAYOUT.subtitles.top,
            bottom: MATERIAL_VIDEO_LAYOUT.subtitles.bottom,
            overflow: "hidden",
            scrollBehavior: "auto",
            outline: showDebugGuides
              ? "3px solid rgba(34, 197, 94, 0.9)"
              : "none",
            outlineOffset: showDebugGuides ? "-3px" : 0,
          }}
        >
          {showDebugGuides ? (
            <div
              data-subtitle-center-guide="true"
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: "50%",
                borderTop: "2px dashed rgba(34, 197, 94, 0.95)",
                zIndex: 50,
                pointerEvents: "none",
              }}
            />
          ) : null}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              gap: MATERIAL_VIDEO_LAYOUT.subtitles.gap,
              paddingTop: timelineViewportPaddingTop,
              paddingBottom: timelineViewportPaddingBottom,
              transform: `translateY(${subtitleTrackShift + interpolate(revealProgress, [0, 1], [MATERIAL_VIDEO_LAYOUT.subtitles.revealOffsetY, 0])}px)`,
              overflow: "visible",
              transformOrigin: "center top",
              opacity: interpolate(
                revealProgress,
                [0, 1],
                [MATERIAL_VIDEO_LAYOUT.subtitles.revealOpacityStart, 1],
              ),
            }}
          >
            {visibleTimelineGroups.map(({ group, index }) => {
              const isGroupActive = activeGroupIndices.includes(index);
              const groupHasActiveWord = group.words.some((word) =>
                highlightState.activeKeys.has(`${group.id}:${word.id}`),
              );
              const groupHasHighlightedWord = group.words.some((word) => {
                const wordKey = `${group.id}:${word.id}`;
                return (
                  highlightState.activeKeys.has(wordKey) ||
                  highlightState.completedKeys.has(wordKey)
                );
              });
              const groupCenter = (group.start + group.end) / 2;
              const focusFactor = Math.max(
                0,
                1 - Math.min(1, Math.abs(currentTime - groupCenter) / 1.2),
              );
              const rowScale = interpolate(
                focusFactor,
                [0, 1],
                [
                  MATERIAL_VIDEO_LAYOUT.row.scaleMin,
                  MATERIAL_VIDEO_LAYOUT.row.scaleMax,
                ],
                {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                },
              );
              const rowOpacity = interpolate(
                focusFactor,
                [0, 1],
                [
                  MATERIAL_VIDEO_LAYOUT.row.opacityMin,
                  MATERIAL_VIDEO_LAYOUT.row.opacityMax,
                ],
                {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                },
              );

              return (
                <div
                  key={group.id}
                  style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    paddingTop: MATERIAL_VIDEO_LAYOUT.row.paddingTop,
                    paddingBottom: MATERIAL_VIDEO_LAYOUT.row.paddingBottom,
                    paddingLeft: showTimestamp
                      ? MATERIAL_VIDEO_LAYOUT.row.paddingLeftWithTimestamp
                      : MATERIAL_VIDEO_LAYOUT.row.paddingLeftDefault,
                    paddingRight: MATERIAL_VIDEO_LAYOUT.row.paddingRight,
                    borderRadius: MATERIAL_VIDEO_LAYOUT.row.borderRadius,
                    backgroundColor: groupHasActiveWord
                      ? MATERIAL_VIDEO_LAYOUT.row.activeBackgroundColor
                      : "transparent",
                    boxShadow: groupHasActiveWord
                      ? "0 12px 28px rgba(125, 54, 88, 0.08)"
                      : "none",
                    transform: `scale(${rowScale})`,
                    opacity: rowOpacity,
                    overflow: "visible",
                  }}
                >
                  {showTimestamp ? (
                    <span
                      style={{
                        position: "absolute",
                        left: 0,
                        top: MATERIAL_VIDEO_LAYOUT.row.timestampTop,
                        fontSize: MATERIAL_VIDEO_LAYOUT.row.timestampFontSize,
                        fontWeight: 600,
                        color: isGroupActive
                          ? MATERIAL_VIDEO_LAYOUT.row.timestampActiveColor
                          : MATERIAL_VIDEO_LAYOUT.row.timestampInactiveColor,
                        letterSpacing: 0.3,
                        zIndex: 1,
                      }}
                    >
                      {formatMaterialVideoClock(group.start)}
                    </span>
                  ) : null}

                  {group.words.length ? (
                    <div
                      style={{
                        position: "relative",
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        alignContent: "flex-start",
                        overflow: "visible",
                        rowGap: MATERIAL_VIDEO_LAYOUT.word.rowGap,
                        columnGap: MATERIAL_VIDEO_LAYOUT.word.columnGap,
                        zIndex: 1,
                      }}
                    >
                      {group.words.map((word) => {
                        const wordKey = `${group.id}:${word.id}`;
                        const isPlaybackActive = wordKey === activeWordKey;
                        const isWordActive =
                          isPlaybackActive ||
                          highlightState.completedKeys.has(wordKey);
                        const isCompletedOnly =
                          karaokeMode &&
                          highlightState.completedKeys.has(wordKey) &&
                          !highlightState.activeKeys.has(wordKey);
                        const isHighlighted = isWordActive || isCompletedOnly;
                        const shouldShowFurigana =
                          showFurigana &&
                          materialVideoHasCjk(word.text) &&
                          Boolean(word.furigana) &&
                          word.furigana !== word.text;
                        const shouldShowRomaji =
                          showRomaji && Boolean(word.romaji);
                        const focusProgress = Math.max(
                          0,
                          1 -
                            Math.min(
                              1,
                              Math.abs(
                                currentTime - (word.start + word.end) / 2,
                              ) / Math.max(0.12, (word.end - word.start) * 0.8),
                            ),
                        );
                        const wordScale = isWordActive
                          ? interpolate(
                              focusProgress,
                              [0, 1],
                              [
                                MATERIAL_VIDEO_LAYOUT.word.focusScaleMin,
                                MATERIAL_VIDEO_LAYOUT.word.focusScaleMax,
                              ],
                              {
                                extrapolateLeft: "clamp",
                                extrapolateRight: "clamp",
                              },
                            )
                          : 1;
                        const plainColor = isGroupActive
                          ? MATERIAL_VIDEO_LAYOUT.word.textActiveRowColor
                          : MATERIAL_VIDEO_LAYOUT.word.textInactiveColor;

                        return (
                          <span
                            data-active-word={
                              isPlaybackActive ? "true" : "false"
                            }
                            data-word-key={wordKey}
                            key={word.id}
                            ref={(element) => {
                              wordRefs.current[wordKey] = element;
                            }}
                            style={{
                              position: "relative",
                              display: "inline-grid",
                              gridTemplateRows: "auto auto auto",
                              alignItems: "center",
                              justifyItems: "center",
                              rowGap: 6,
                              marginInline: 0,
                              borderRadius:
                                MATERIAL_VIDEO_LAYOUT.word.borderRadius,
                              padding: `${MATERIAL_VIDEO_LAYOUT.word.paddingY}px ${MATERIAL_VIDEO_LAYOUT.word.paddingX}px`,
                              color: isHighlighted
                                ? MATERIAL_VIDEO_LAYOUT.word
                                    .textHighlightedColor
                                : plainColor,
                              backgroundColor: "transparent",
                              boxShadow: "none",
                              transform: `scale(${wordScale})`,
                              zIndex: isPlaybackActive ? 3 : 2,
                              overflow: "visible",
                              outline:
                                showDebugGuides && isPlaybackActive
                                  ? "3px solid rgba(239, 68, 68, 0.95)"
                                  : "none",
                              outlineOffset:
                                showDebugGuides && isPlaybackActive ? "3px" : 0,
                            }}
                          >
                            <span
                              aria-hidden={!shouldShowFurigana}
                              style={{
                                minHeight:
                                  MATERIAL_VIDEO_LAYOUT.word.furiganaFontSize,
                                whiteSpace: "nowrap",
                                fontSize:
                                  MATERIAL_VIDEO_LAYOUT.word.furiganaFontSize,
                                lineHeight: 1,
                                fontWeight: 600,
                                letterSpacing:
                                  MATERIAL_VIDEO_LAYOUT.word
                                    .annotationLetterSpacing,
                                color: "#9ca3af",
                                visibility: shouldShowFurigana
                                  ? "visible"
                                  : "hidden",
                              }}
                            >
                              {shouldShowFurigana ? word.furigana : " "}
                            </span>
                            <span
                              style={{
                                position: "relative",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight:
                                  MATERIAL_VIDEO_LAYOUT.word.textFontSize,
                                borderRadius:
                                  MATERIAL_VIDEO_LAYOUT.word.borderRadius,
                                padding: 0,
                                fontSize:
                                  MATERIAL_VIDEO_LAYOUT.word.textFontSize,
                                lineHeight: 1,
                                fontWeight: isHighlighted ? 700 : 600,
                                letterSpacing:
                                  MATERIAL_VIDEO_LAYOUT.word.textLetterSpacing,
                                backgroundColor: "transparent",
                                boxShadow: "none",
                              }}
                            >
                              {isHighlighted ? (
                                <span
                                  aria-hidden="true"
                                  style={{
                                    position: "absolute",
                                    inset: "-8px -12px",
                                    borderRadius:
                                      MATERIAL_VIDEO_LAYOUT.word.borderRadius,
                                    backgroundColor:
                                      MATERIAL_VIDEO_LAYOUT.word
                                        .highlightBackgroundColor,
                                    boxShadow: `0 6px 14px ${MATERIAL_VIDEO_LAYOUT.word.highlightShadowColor}`,
                                    zIndex: -1,
                                  }}
                                />
                              ) : null}
                              {word.text}
                            </span>
                            <span
                              aria-hidden={!shouldShowRomaji}
                              style={{
                                minHeight:
                                  MATERIAL_VIDEO_LAYOUT.word.romajiFontSize,
                                whiteSpace: "nowrap",
                                fontSize:
                                  MATERIAL_VIDEO_LAYOUT.word.romajiFontSize,
                                lineHeight: 1,
                                letterSpacing:
                                  MATERIAL_VIDEO_LAYOUT.word
                                    .annotationLetterSpacing,
                                color: "rgba(156, 163, 175, 0.92)",
                                visibility: shouldShowRomaji
                                  ? "visible"
                                  : "hidden",
                              }}
                            >
                              {shouldShowRomaji ? word.romaji : " "}
                            </span>
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <p
                      style={{
                        position: "relative",
                        zIndex: 1,
                        margin: 0,
                        fontSize: 38,
                        lineHeight: 1.46,
                        letterSpacing: 0.4,
                        color: groupHasHighlightedWord
                          ? MATERIAL_VIDEO_LAYOUT.word.textHighlightedColor
                          : "rgba(152, 162, 179, 0.88)",
                      }}
                    >
                      {group.sourceText ||
                        group.subtitle ||
                        group.words.map((word) => word.text).join(" ")}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div
          style={{
            position: "absolute",
            left: 68,
            right: 68,
            bottom: 68,
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <div
            style={{
              width: "100%",
              borderRadius: 0,
              padding: 0,
              backgroundColor: "transparent",
              boxShadow: "none",
              backdropFilter: "none",
              transform: `translateY(${interpolate(revealProgress, [0, 1], [10, 0])}px)`,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: MATERIAL_VIDEO_LAYOUT.fallbackTitle.fontSize,
                lineHeight: MATERIAL_VIDEO_LAYOUT.fallbackTitle.lineHeight,
                fontWeight: 700,
                color: MATERIAL_VIDEO_LAYOUT.fallbackTitle.color,
                letterSpacing: 0.4,
              }}
            >
              {activeHeadline}
            </p>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
}
